import {
  BONUS,
  SESSION_KEYS,
  USER_PROFILE_DATA,
  SITE_TECHNICAL_DATA,
  UI_FLOW_PREFERENCES,
} from "src/consts/session";
import {
  leadTransfer,
  tariffSavings,
  TariffSavingsPayload,
} from "src/api/apiClients";
import { format } from "date-fns";
import { useCallback, useEffect } from "react";
import { PvSetupTypes } from "src/pages/PvSetup";
import { WallboxTypes } from "src/pages/Wallbox";
import { SmartMeterTypes } from "src/pages/SmartMeter";
import { useSessionStorage } from "./useSessionStorage";
import { numberFormat } from "src/helpers/numberFormat";
import { BatterySetupTypes } from "src/pages/BatterySetup";
import { LeadTransfer, Tariff } from "src/models/clientsModel";
import { useLocation, useSearchParams } from "react-router-dom";
import { tariffSavingsMapped } from "src/dto/clientsModelMapped";
import { MOBILE_NUMBER_PREFIX } from "src/pages/Introduction/Introduction";
import { leadSelfOnboardingTransfer } from "src/api/selfOnboardingClients";

enum OEMs {
  VIESSMANN_API = "VIESSMANN_API",
  SUNGROW_ISOLARCLOUD = "SUNGROW_ISOLARCLOUD",
  ENPH_VPPAPI_SELF = "ENPH_VPPAPI_SELF",
}

const useLeadTransfer = () => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const transferId = searchParams.get("tid") || "";
  const salesPartnerUuid = searchParams.get("spid") || "";
  const selfOnboardingOemSelected =
    searchParams.get("selfOnboardingOemSelected") || "";
  const isOemPreselected =
    selfOnboardingOemSelected === OEMs.VIESSMANN_API ||
    selfOnboardingOemSelected === OEMs.SUNGROW_ISOLARCLOUD ||
    selfOnboardingOemSelected === OEMs.ENPH_VPPAPI_SELF;

  const [, setTransferId] = useSessionStorage<string>(SESSION_KEYS.TRANSFER_ID);
  const [, setTariffs] = useSessionStorage<Tariff[]>(SESSION_KEYS.TARIFFS);
  const [, setBonus] = useSessionStorage<BONUS | null>(SESSION_KEYS.BONUS);

  const [, setSalesPartnerId] = useSessionStorage<string>(
    SESSION_KEYS.SALES_PARTNER_ID,
  );
  const [, setSiteCorrelationId] = useSessionStorage<string>(
    SESSION_KEYS.SITE_CORRELATION_ID,
  );
  const [, setUiFlowPreferences] = useSessionStorage<UI_FLOW_PREFERENCES>(
    SESSION_KEYS.UI_FLOW_PREFERENCES,
  );
  const [, setUserProfileData] = useSessionStorage<USER_PROFILE_DATA>(
    SESSION_KEYS.USER_PROFILE_DATA,
  );
  const [, setSiteTechnicalData] = useSessionStorage<SITE_TECHNICAL_DATA>(
    SESSION_KEYS.SITE_TECHNICAL_DATA,
  );
  const [, setOemSelected] = useSessionStorage<string>(
    SESSION_KEYS.SELF_ONBOARDING_OEM_SELECTED,
  );

  const getErrorStatus = (error: any): number | null => {
    if (error?.response?.status) {
      return error.response.status;
    }
    if (error?.status) {
      return error.status;
    }
    if (error?.statusCode) {
      return error.statusCode;
    }
    return null;
  };

  const getLeadTransfer = useCallback(async () => {
    if (pathname.includes("smart-charging")) return;
    if (pathname.includes("site-authentication")) return;
    if (pathname.includes("selfonboarding")) return;

    if (isOemPreselected) {
      try {
        const response = await leadSelfOnboardingTransfer({
          transferId,
          salesPartnerUuid,
        });
        setTransferId(transferId);
        setSalesPartnerId(salesPartnerUuid);
        setSiteCorrelationId(response.siteCorrelationId);
        setUiFlowPreferences({
          language: response.uiFlowPreferences.language,
          callbackUrlSuccess: response.uiFlowPreferences.callbackUrlSuccess,
          callbackUrlFailure: response.uiFlowPreferences.callbackUrlFailure,
          processType: response.uiFlowPreferences.processType,
          skipOemAuth: response.uiFlowPreferences.skipOemAuth.toString(),
        });
        setSiteTechnicalData({
          pvSetup: {
            pvInstalled: response.siteTechnicalData.pvInstalled
              ? PvSetupTypes.JA
              : PvSetupTypes.NEIN,

            pvPowerMaxKwp: numberFormat(
              response.siteTechnicalData.pvPowerMaxKwp
                ? response.siteTechnicalData.pvPowerMaxKwp.toString()
                : "",
            ),
          },

          wallboxInstalled: response.siteTechnicalData.wallboxInstalled
            ? WallboxTypes.JA
            : WallboxTypes.NEIN,

          batterySetup: {
            batteryInstalled: response.siteTechnicalData.batteryInstalled
              ? BatterySetupTypes.JA
              : BatterySetupTypes.NEIN,

            batteryCapacity: numberFormat(
              response.siteTechnicalData.batterySizeKwh
                ? response.siteTechnicalData.batterySizeKwh.toString()
                : "",
            ),
          },

          smartMeterInstalled: response.siteTechnicalData.smartMeterInstalled
            ? SmartMeterTypes.JA
            : SmartMeterTypes.NEIN,
          heatpumpInstalled:
            response.siteTechnicalData.heatpumpInstalled?.toString(),
          marketLocationId: response.siteTechnicalData.marketLocationId,
          meterNumber: response.siteTechnicalData.meterNumber,
        });
        if (response.siteTechnicalData.hemProviderLogicalId) {
          setOemSelected(response.siteTechnicalData.hemProviderLogicalId);
        }
      } catch (e) {}

      return;
    }

    try {
      const response = await leadTransfer({ transferId, salesPartnerUuid });

      setTransferId(transferId);
      setSalesPartnerId(salesPartnerUuid);
      setSiteCorrelationId(response.siteCorrelationId);
      setUiFlowPreferences({
        language: response.uiFlowPreferences.language,
        callbackUrlSuccess: response.uiFlowPreferences.callbackUrlSuccess,
        callbackUrlFailure: response.uiFlowPreferences.callbackUrlFailure,
      });
      setUserProfileData({
        firstName: response.userProfileData.firstname,
        lastName: response.userProfileData.lastname,
        country: response.userProfileData.country,
        zipCode: response.userProfileData.zipcode,
        city: response.userProfileData.city,
        street: response.userProfileData.street,
        houseNumber: response.userProfileData.streetNumber,
        email: response.userProfileData.emailAddress,
        mobileNumber:
          response.userProfileData.mobilePhone ?? MOBILE_NUMBER_PREFIX,
        yearlyGridSupplyKwh: numberFormat(
          response.userProfileData.yearlyGridSupplyKwh
            ? response.userProfileData.yearlyGridSupplyKwh.toString()
            : "",
        ),
        confirmEmail: response.userProfileData.emailAddress,

        pvInstallationYear: response.siteTechnicalData.pvInstallationDate
          ? format(response.siteTechnicalData.pvInstallationDate, "yyyy")
          : "",
      });
      setSiteTechnicalData({
        pvSetup: {
          pvInstalled: response.siteTechnicalData.pvInstalled
            ? PvSetupTypes.JA
            : PvSetupTypes.NEIN,

          pvPowerMaxKwp: numberFormat(
            response.siteTechnicalData.pvPowerMaxKwp
              ? response.siteTechnicalData.pvPowerMaxKwp.toString()
              : "",
          ),

          postalCode: response.userProfileData.zipcode,
        },

        wallboxInstalled: response.siteTechnicalData.wallboxInstalled
          ? WallboxTypes.JA
          : WallboxTypes.NEIN,

        batterySetup: {
          batteryInstalled: response.siteTechnicalData.batteryInstalled
            ? BatterySetupTypes.JA
            : BatterySetupTypes.NEIN,

          batteryCapacity: numberFormat(
            response.siteTechnicalData.batterySizeKwh
              ? response.siteTechnicalData.batterySizeKwh.toString()
              : "",
          ),
        },

        smartMeterInstalled: response.siteTechnicalData.smartMeterInstalled
          ? SmartMeterTypes.JA
          : SmartMeterTypes.NEIN,
        heatpumpInstalled:
          response.siteTechnicalData.heatpumpInstalled?.toString(),
        marketLocationId: response.siteTechnicalData.marketLocationId,
        meterNumber: response.siteTechnicalData.meterNumber,
      });

      await getTariffSavings(response);
    } catch (e) {
      setSiteCorrelationId("not found");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTariffSavings = useCallback(
    async (response: LeadTransfer) => {
      const pvInstalled = response.siteTechnicalData.pvInstalled ?? false;
      const pvPowerMaxKwp = pvInstalled
        ? response.siteTechnicalData.pvPowerMaxKwp
          ? response.siteTechnicalData.pvPowerMaxKwp
          : null
        : null;
      const wallboxInstalled =
        response.siteTechnicalData.wallboxInstalled ?? false;
      const batteryInstalled =
        response.siteTechnicalData.batteryInstalled ?? false;
      const batterySizeKwh = batteryInstalled
        ? response.siteTechnicalData.batterySizeKwh
          ? response.siteTechnicalData.batterySizeKwh
          : null
        : null;
      const smartMeterInstalled =
        response.siteTechnicalData.smartMeterInstalled ?? false;
      const yearlyGridSupplyKwh = response.userProfileData.yearlyGridSupplyKwh
        ? +response.userProfileData.yearlyGridSupplyKwh
        : 0;
      const zipcode = response.userProfileData.zipcode ?? "";

      const payload: TariffSavingsPayload = {
        siteTechnicalData: {
          pvInstalled,
          pvPowerMaxKwp,
          batterySizeKwh,
          batteryInstalled,
          wallboxInstalled,
          smartMeterInstalled,
        },
        userProfileData: {
          zipcode,
          yearlyGridSupplyKwh,
        },
      };

      try {
        const response = await tariffSavings(payload);
        const responseMapped = tariffSavingsMapped(response);
        setTariffs(responseMapped);
        if (response.bonus && response.bonus.isActive) {
          setBonus(response.bonus);
        } else {
          setBonus(null);
        }
      } catch (e: any) {
        const status = getErrorStatus(e);

        if (status === 400) {
          setTariffs([]);
          setBonus(null);
        }
      }
    },
    [setBonus, setTariffs],
  );

  useEffect(() => {
    getLeadTransfer();
  }, [getLeadTransfer]);
};

export default useLeadTransfer;
